import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import { subscriptionAdminApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';

function formatHours(seconds) {
  if (seconds == null) return '—';
  const h = Number(seconds) / 3600;
  return `${h.toFixed(1)} h`;
}

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [addonPlans, setAddonPlans] = useState([]);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeForm, setConsumeForm] = useState({ id: '', seconds: 3600, sessionRef: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canEdit = canWrite('subscriptions');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = userIdFilter.trim() ? { userId: userIdFilter.trim() } : undefined;
      const [data, addons] = await Promise.all([
        subscriptionAdminApi.listSubscriptions(params),
        subscriptionAdminApi.listPlans({ planCategory: 'addon' }).catch(() => []),
      ]);
      setSubscriptions(Array.isArray(data) ? data : []);
      setAddonPlans(Array.isArray(addons) ? addons : []);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [userIdFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const viewUsage = async (id) => {
    try {
      const data = await subscriptionAdminApi.getSubscriptionUsage(id);
      setUsage(data);
      setUsageOpen(true);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  const consume = async () => {
    try {
      await subscriptionAdminApi.consumeSubscription(consumeForm.id, {
        seconds: Number(consumeForm.seconds),
        ...(consumeForm.sessionRef ? { sessionRef: consumeForm.sessionRef } : {}),
      });
      setSnackbar({ open: true, type: 'success', message: 'Usage consumed.' });
      setConsumeOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  const cancelSub = async (id) => {
    if (!window.confirm('Cancel this subscription record?')) return;
    try {
      await subscriptionAdminApi.cancelSubscription(id);
      setSnackbar({ open: true, type: 'success', message: 'Subscription cancelled.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  return (
    <PageShell
      title="Step 5 — Subscriptions"
      subtitle="GET /subscriptions · GET /subscriptions/:id/usage · POST .../consume"
      actions={
        <TextField
          size="small"
          label="Filter by user ID"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
      }
    >
      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((s) => (
                <TableRow key={s._id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{String(s._id).slice(-8)}</TableCell>
                  <TableCell>{String(s.userId || '').slice(-8)}</TableCell>
                  <TableCell>{String(s.contractId || '').slice(-8)}</TableCell>
                  <TableCell><Chip label={s.status || '—'} size="small" /></TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => viewUsage(s._id)}>Usage</Button>
                    {canEdit && (
                      <>
                        <Button
                          size="small"
                          onClick={() => {
                            setConsumeForm({ id: s._id, seconds: 3600, sessionRef: '' });
                            setConsumeOpen(true);
                          }}
                        >
                          Consume
                        </Button>
                        <Button size="small" color="warning" onClick={() => cancelSub(s._id)}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!subscriptions.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b' }}>
                    No subscriptions. Complete Step 4 payment first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={usageOpen} onClose={() => setUsageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Usage summary</DialogTitle>
        <DialogContent>
          {usage ? (
            <>
              <Typography variant="body2">Quota (seconds): {usage.quotaSeconds ?? '—'}</Typography>
              <Typography variant="body2">Used (seconds): {usage.usedSeconds ?? '—'}</Typography>
              <Typography variant="body2">Remaining: {formatHours(usage.remainingSeconds)}</Typography>
            </>
          ) : (
            <Typography>No usage data.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={consumeOpen} onClose={() => setConsumeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Consume usage</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Seconds"
            type="number"
            value={consumeForm.seconds}
            onChange={(e) => setConsumeForm({ ...consumeForm, seconds: e.target.value })}
          />
          <TextField
            label="Session ref (optional)"
            value={consumeForm.sessionRef}
            onChange={(e) => setConsumeForm({ ...consumeForm, sessionRef: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsumeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={consume}>Deduct</Button>
        </DialogActions>
      </Dialog>

      {addonPlans.length > 0 && (
        <Typography variant="caption" sx={{ mt: 2, display: 'block', color: '#64748b' }}>
          Addon plans available for POST /subscriptions/:id/addons/purchase:{' '}
          {addonPlans.map((p) => p.planName).join(', ')}
        </Typography>
      )}

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
